import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminSidebar from "../../components/admin/AdminSidebar";
import { ShoppingCart, CheckCircle, XCircle, Eye } from "lucide-react";
import { useModal } from "../../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminOrders() {
  const { showAlert } = useModal();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/admin/orders`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setOrders(res.data);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${apiUrl}/api/admin/orders/${id}/status`, { status }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      fetchOrders();
    } catch (err) {
      showAlert("Failed to update status", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <main className="ml-64 flex-grow p-8">
        <header className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Orders</h1>
            <p className="text-gray-500">Track and fulfill customer requests.</p>
          </div>
          <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 flex items-center gap-2">
            <ShoppingCart className="text-purple-500" size={18} />
            <span className="text-sm font-bold text-gray-700">
              {orders.length} Total Orders
            </span>
          </div>
        </header>

        {loading ? (
          <div className="text-gray-500">Loading orders...</div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-600 text-sm uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Order</th>
                    <th className="px-6 py-4 font-semibold">Customer</th>
                    <th className="px-6 py-4 font-semibold">Specs</th>
                    <th className="px-6 py-4 font-semibold">Price</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((o) => (
                    <tr key={o.ID} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={o.Image ? o.Image : "/placeholder.jpg"} 
                            className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                            alt="" 
                          />
                          <div>
                            <p className="font-bold text-gray-900">{o.ItemName}</p>
                            <p className="text-xs text-gray-500">#{o.ID}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{o.userName}</p>
                        <p className="text-xs text-gray-500">{o.userEmail}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-gray-600 space-y-0.5">
                          {o.Storage && <span>Storage: {o.Storage} </span>}
                          {o.Ram && <span>RAM: {o.Ram} </span>}
                          {o.Color && <span>Color: {o.Color} </span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-green-600">
                        {Number(o.Price)?.toLocaleString()} ETB
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          o.status === 'completed' 
                            ? "bg-green-100 text-green-700" 
                            : o.status === 'cancelled'
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => updateStatus(o.ID, 'completed')}
                            className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            title="Mark Completed"
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button 
                            onClick={() => updateStatus(o.ID, 'cancelled')}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                            title="Cancel Order"
                          >
                            <XCircle size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
